import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

export type ValidationIssue = {
  id: string;
  donor: string;
  message: string;
  isEscalated?: boolean;
  escalatedBy?: string;
  escalationNote?: string;
};

export type Revision = {
  id: string;
  donor: string;
  revisionComment?: string;
};

export type ValidationWarning = {
  id: string;
  donor: string;
  message: string;
};

export type DataIssue = {
  id: string;
  donor: string;
  breed: string;
  reason: string;
};

export type AlertTabType =
  | 'issues'
  | 'revisions'
  | 'warnings'
  | 'dataIssues'
  | null;

export type AlertPillsProps = {
  issues: ValidationIssue[];
  revisions: Revision[];
  warnings: ValidationWarning[];
  dataIssues: DataIssue[];
  escalatedCount?: number;
  activeTab: AlertTabType;
  onTabChange: (tab: AlertTabType) => void;
};

export const AlertPills = ({
  issues,
  revisions,
  warnings,
  dataIssues,
  escalatedCount = 0,
  activeTab,
  onTabChange,
}: AlertPillsProps) => {
  const { t } = useTranslation();

  const hasAnyAlerts =
    issues.length > 0 ||
    revisions.length > 0 ||
    warnings.length > 0 ||
    dataIssues.length > 0 ||
    escalatedCount > 0;

  if (!hasAnyAlerts) return null;

  const handleTabClick = (tab: AlertTabType) => {
    onTabChange(activeTab === tab ? null : tab);
  };

  return (
    <div className="flex flex-wrap gap-2">
      {issues.length > 0 && (
        <button
          type="button"
          onClick={() => handleTabClick('issues')}
          className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
            activeTab === 'issues'
              ? 'border-red-300 bg-red-100 text-red-700'
              : 'border-gray-200 bg-white text-gray-600 hover:border-red-200 hover:bg-red-50 hover:text-red-600'
          }`}
        >
          <i className="pi pi-exclamation-circle text-red-500" />
          <span>
            {t(
              'fertWork.issueCount',
              '{count, plural, one {# Issue} other {# Issues}}',
              { count: issues.length },
            )}
          </span>
          <i
            className={`pi ${activeTab === 'issues' ? 'pi-chevron-up' : 'pi-chevron-down'} text-xs`}
          />
        </button>
      )}

      {revisions.length > 0 && (
        <button
          type="button"
          onClick={() => handleTabClick('revisions')}
          className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
            activeTab === 'revisions'
              ? 'border-blue-300 bg-blue-100 text-blue-700'
              : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600'
          }`}
        >
          <i className="pi pi-info-circle text-blue-500" />
          <span>
            {t(
              'fertWork.revisionCount',
              '{count, plural, one {# Revision} other {# Revisions}}',
              { count: revisions.length },
            )}
          </span>
          <i
            className={`pi ${activeTab === 'revisions' ? 'pi-chevron-up' : 'pi-chevron-down'} text-xs`}
          />
        </button>
      )}

      {warnings.length > 0 && (
        <button
          type="button"
          onClick={() => handleTabClick('warnings')}
          className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
            activeTab === 'warnings'
              ? 'border-amber-300 bg-amber-100 text-amber-700'
              : 'border-gray-200 bg-white text-gray-600 hover:border-amber-200 hover:bg-amber-50 hover:text-amber-600'
          }`}
        >
          <i className="pi pi-exclamation-triangle text-amber-500" />
          <span>
            {t(
              'fertWork.warningCount',
              '{count, plural, one {# Warning} other {# Warnings}}',
              { count: warnings.length },
            )}
          </span>
          <i
            className={`pi ${activeTab === 'warnings' ? 'pi-chevron-up' : 'pi-chevron-down'} text-xs`}
          />
        </button>
      )}

      {dataIssues.length > 0 && (
        <button
          type="button"
          onClick={() => handleTabClick('dataIssues')}
          className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
            activeTab === 'dataIssues'
              ? 'border-purple-300 bg-purple-100 text-purple-700'
              : 'border-gray-200 bg-white text-gray-600 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-600'
          }`}
        >
          <i className="pi pi-database text-purple-500" />
          <span>
            {t(
              'fertWork.dataIssueCount',
              '{count, plural, one {# Data Issue} other {# Data Issues}}',
              { count: dataIssues.length },
            )}
          </span>
          <i
            className={`pi ${activeTab === 'dataIssues' ? 'pi-chevron-up' : 'pi-chevron-down'} text-xs`}
          />
        </button>
      )}

      {escalatedCount > 0 && (
        <span className="flex items-center gap-2 rounded-full border border-orange-300 bg-orange-100 px-3 py-1.5 text-sm font-medium text-orange-700">
          <i className="pi pi-flag text-orange-500" />
          <span>
            {t('fertWork.escalatedLabel', '{count} Escalated', {
              count: escalatedCount,
            })}
          </span>
        </span>
      )}
    </div>
  );
};

export type AlertPanelProps = {
  activeTab: AlertTabType;
  issues: ValidationIssue[];
  selectedIssues: string[];
  onSelectedIssuesChange: (selectedIssues: string[]) => void;
  onViewIssue: (issueId: string) => void;
  onEscalateIssue?: (issueId: string) => void;
  onResolveEscalation?: (issueId: string) => void;
  onFixSelectedIssues?: () => void;
  revisions: Revision[];
  onViewRevision: (revisionId: string) => void;
  warnings: ValidationWarning[];
  selectedWarnings: string[];
  onSelectedWarningsChange: (selectedWarnings: string[]) => void;
  onFixWarning: (warningId: string) => void;
  onFixSelectedWarnings?: () => void;
  dataIssues: DataIssue[];
};

export const AlertPanel = ({
  activeTab,
  issues,
  selectedIssues,
  onSelectedIssuesChange,
  onViewIssue,
  onEscalateIssue,
  onResolveEscalation,
  onFixSelectedIssues,
  revisions,
  onViewRevision,
  warnings,
  selectedWarnings,
  onSelectedWarningsChange,
  onFixWarning,
  onFixSelectedWarnings,
  dataIssues,
}: AlertPanelProps) => {
  const { t } = useTranslation();

  if (!activeTab) return null;

  const handleToggleIssue = (issueId: string) => {
    const newSelection = selectedIssues.includes(issueId)
      ? selectedIssues.filter((id) => id !== issueId)
      : [...selectedIssues, issueId];
    onSelectedIssuesChange(newSelection);
  };

  const handleToggleAllIssues = () => {
    if (selectedIssues.length === issues.length) {
      onSelectedIssuesChange([]);
    } else {
      onSelectedIssuesChange(issues.map((issue) => issue.id));
    }
  };

  const handleToggleWarning = (warningId: string) => {
    const newSelection = selectedWarnings.includes(warningId)
      ? selectedWarnings.filter((id) => id !== warningId)
      : [...selectedWarnings, warningId];
    onSelectedWarningsChange(newSelection);
  };

  const handleToggleAllWarnings = () => {
    if (selectedWarnings.length === warnings.length) {
      onSelectedWarningsChange([]);
    } else {
      onSelectedWarningsChange(warnings.map((warning) => warning.id));
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="max-h-64 overflow-y-auto p-3">
        {activeTab === 'issues' && issues.length > 0 && (
          <div className="space-y-1">
            <div className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2">
              <Checkbox
                checked={selectedIssues.length === issues.length}
                indeterminate={
                  selectedIssues.length > 0 &&
                  selectedIssues.length < issues.length
                }
                onCheckedChange={handleToggleAllIssues}
              />
              <span className="text-sm text-gray-500">
                {t('fertWork.selectAll', 'Select all')}
              </span>
              {onFixSelectedIssues && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="ml-auto"
                  onClick={onFixSelectedIssues}
                  disabled={selectedIssues.length === 0}
                >
                  <i className="pi pi-wrench" data-icon="inline-start" />
                  {t('fertWork.fixIssues', 'Fix Issues')}
                </Button>
              )}
            </div>
            {issues.map((issue) => (
              <div
                key={issue.id}
                className={`flex items-center gap-2 rounded p-2 ${issue.isEscalated ? 'border border-orange-200 bg-orange-50' : 'bg-gray-50'}`}
              >
                <Checkbox
                  checked={selectedIssues.includes(issue.id)}
                  onCheckedChange={() => handleToggleIssue(issue.id)}
                />
                {issue.isEscalated ? (
                  <i className="pi pi-flag text-sm text-orange-500" />
                ) : (
                  <i className="pi pi-exclamation-circle text-sm text-red-500" />
                )}
                <span className="flex-1 text-sm">
                  <strong>{issue.donor}</strong> - {issue.message}
                  {issue.isEscalated && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-700">
                      {t('fertWork.escalated', 'Escalated')}
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  {issue.isEscalated ? (
                    <button
                      type="button"
                      className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-orange-600 transition-colors hover:text-orange-800 hover:underline"
                      onClick={() => onResolveEscalation?.(issue.id)}
                    >
                      {t('fertWork.resolve', 'Resolve')}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-orange-600 transition-colors hover:text-orange-800 hover:underline"
                        onClick={() => onEscalateIssue?.(issue.id)}
                      >
                        {t('fertWork.escalate', 'Escalate')}
                      </button>
                      <button
                        type="button"
                        className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                        onClick={() => onViewIssue(issue.id)}
                      >
                        {t('fertWork.fix', 'Fix')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'revisions' && revisions.length > 0 && (
          <div className="space-y-1">
            {revisions.map((revision) => (
              <div
                key={revision.id}
                className="flex items-center gap-2 rounded bg-gray-50 p-2"
              >
                <i className="pi pi-info-circle text-sm text-blue-500" />
                <span className="flex-1 text-sm">
                  <strong>{revision.donor}</strong>
                  {revision.revisionComment && (
                    <span className="ml-1 text-gray-500">
                      - {revision.revisionComment}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                  onClick={() => onViewRevision(revision.id)}
                >
                  {t('common.view', 'View')}
                </button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'warnings' && warnings.length > 0 && (
          <div className="space-y-1">
            <div className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2">
              <Checkbox
                checked={selectedWarnings.length === warnings.length}
                indeterminate={
                  selectedWarnings.length > 0 &&
                  selectedWarnings.length < warnings.length
                }
                onCheckedChange={handleToggleAllWarnings}
              />
              <span className="text-sm text-gray-500">
                {t('fertWork.selectAll', 'Select all')}
              </span>
              {onFixSelectedWarnings && (
                <Button
                  variant="warning"
                  size="sm"
                  className="ml-auto"
                  onClick={onFixSelectedWarnings}
                  disabled={selectedWarnings.length === 0}
                >
                  <i className="pi pi-wrench" data-icon="inline-start" />
                  {t('fertWork.fixWarnings', 'Fix Warnings')}
                </Button>
              )}
            </div>
            {warnings.map((warning) => (
              <div
                key={warning.id}
                className="flex items-center gap-2 rounded bg-gray-50 p-2"
              >
                <Checkbox
                  checked={selectedWarnings.includes(warning.id)}
                  onCheckedChange={() => handleToggleWarning(warning.id)}
                />
                <i className="pi pi-exclamation-triangle text-sm text-amber-500" />
                <span className="flex-1 text-sm">
                  <strong>{warning.donor}</strong> - {warning.message}
                </span>
                <button
                  type="button"
                  className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                  onClick={() => onFixWarning(warning.id)}
                >
                  {t('fertWork.fix', 'Fix')}
                </button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'dataIssues' && dataIssues.length > 0 && (
          <div className="space-y-1">
            <div className="mb-2 border-b border-gray-100 pb-2 text-sm text-gray-500">
              {t(
                'fertWork.dataIssueSectionHint',
                'Donors excluded from this session due to scheduling-level data errors. Non-blocking - fix in OvaDrive.',
              )}
            </div>
            {dataIssues.map((issue) => (
              <div
                key={issue.id}
                className="flex items-center gap-2 rounded bg-gray-50 p-2"
              >
                <i className="pi pi-database text-sm text-purple-500" />
                <span className="flex-1 text-sm">
                  <strong>{issue.donor}</strong>
                  <span className="ml-1 text-gray-500">
                    -{' '}
                    {t(`fertWork.dataIssueReason.${issue.reason}`, {
                      defaultValue: issue.reason,
                      breed: issue.breed,
                    })}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
